import { OvertimeRequest } from "@/types"
import { toast } from "sonner"
import { supabase } from "@/integrations/supabase/client"
import { OvertimeStatus, UserRole, CompanySettingKey } from "@/types/enums"


// Define a reason structure
interface OvertimeReason {
  main_title: string;
  reason: string | null;
}

/**
 * Hook for overtime request submission functionality
 */
export const useOvertimeRequests = (
  user: { id: string; companyId?: string; email?: string; name?: string; department?: string[]; role?: string } | null,
  setOvertimeRequests: React.Dispatch<React.SetStateAction<OvertimeRequest[]>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) => {
  
  const submitOvertimeRequest = async (employeeId: string, request: { date: string; hours: number; startTime?: string; endTime?: string; justification?: string }): Promise<boolean> => {
    setLoading(true)
    setError(null)
    
    try {
      if (!user?.id) {
        setError("User ID is missing")
        return false
      }
      
      // Get company settings for max overtime - try to use user.companyId if available
      let maxOvertime = 104 // Default value
      
      if (user.companyId) {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select(CompanySettingKey.MAX_OVERTIME_HOURS)
          .eq('id', user.companyId)
          .maybeSingle()
        
        if (!companyError && companyData && (companyData as any)[CompanySettingKey.MAX_OVERTIME_HOURS]) {
          maxOvertime = (companyData as any)[CompanySettingKey.MAX_OVERTIME_HOURS]
        }
      } else {
        // If no company ID, get default from companies table
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select(CompanySettingKey.MAX_OVERTIME_HOURS)
          .limit(1)
          .maybeSingle()
        
        if (!companyError && companyData && (companyData as any)[CompanySettingKey.MAX_OVERTIME_HOURS]) {
          maxOvertime = (companyData as any)[CompanySettingKey.MAX_OVERTIME_HOURS]
        }
      }
      
      // Check if this request requires VP approval
      let vpApprovalRequired = false
      
      // Get approved requests for this employee in the current month
      const now = new Date()
      const currentMonth = now.getMonth()
      const currentYear = now.getFullYear()
      
      const { data: approvedRequests, error: requestsError } = await supabase
        .from('overtime_requests')
        .select('hours, date')
        .eq('employee_id', employeeId)
        .eq('status', OvertimeStatus.APPROVED)
      
      if (requestsError) {
        setError("Could not check existing overtime hours")
        return false
      }
      
      if (!approvedRequests) {
        setError("Could not retrieve overtime history")
        return false
      }
      
      // Calculate total hours for this month
      const monthlyHours = approvedRequests.reduce((total: number, req: { hours: number; date: string }) => {
        const reqDate = new Date(req.date)
        if (reqDate.getMonth() === currentMonth && reqDate.getFullYear() === currentYear) {
          return total + req.hours
        }
        return total
      }, 0)
      
      // Check if this request would exceed the max overtime
      vpApprovalRequired = (monthlyHours + request.hours) > maxOvertime
      
      const requestData = {
        employee_id: employeeId,
        employee_name: user.name || 'Unknown',
        department: user.department && user.department.length > 0 ? user.department[0] : 'Unknown',
        date: request.date,
        hours: request.hours,
        start_time: request.startTime,
        end_time: request.endTime,
        justification: request.justification,
        status: user.role === UserRole.SUPER_ADMIN ? OvertimeStatus.APPROVED : 
                (user.role === UserRole.SUPERINTENDENT ? 
                  (vpApprovalRequired ? OvertimeStatus.PENDING_VP_APPROVAL : OvertimeStatus.PENDING_DH_APPROVAL) : OvertimeStatus.PENDING),
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        vp_approval_required: vpApprovalRequired,
        created_by_id: user.id,
        created_by_name: user.name || 'Unknown',
        created_by_role: user.role || UserRole.EMPLOYEE
      }
      
      const { data, error } = await supabase
        .from('overtime_requests')
        .insert(requestData as any)
        .select()
      
      if (error) {
        setError(`Failed to submit request: ${error.message}`)
        return false
      }
      
      if (!data || data.length === 0) {
        setError("No data returned after submission")
        return false
      }
      
      const newRequest = {
        ...(data[0] as any),
        employeeId: (data[0] as any).employee_id,
        employeeName: (data[0] as any).employee_name,
        startTime: (data[0] as any).start_time,
        endTime: (data[0] as any).end_time,
        submittedAt: (data[0] as any).submitted_at,
        updatedAt: (data[0] as any).updated_at,
        vpApprovalRequired: (data[0] as any).vp_approval_required,
        created_by_id: (data[0] as any).created_by_id,
        created_by_name: (data[0] as any).created_by_name,
        created_by_role: (data[0] as any).created_by_role
      }
      
      setOvertimeRequests(prevRequests => [newRequest, ...prevRequests])
      toast.success("Request submitted successfully!")
      return true
      
    } catch (err: unknown) {
      setError(`Failed to submit overtime request: ${err instanceof Error ? err.message : ''}`)
      return false
    } finally {
      setLoading(false)
    }
  }
  
  const checkEmployeeOvertimeEligibility = async (employeeId: string, hours: number) => {
    try {
      // Get max overtime hours from the companies table
      let maxOvertime = 104 // Default value
      
      if (user && user.companyId) {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select(CompanySettingKey.MAX_OVERTIME_HOURS)
          .eq('id', user.companyId)
          .maybeSingle()
        
        if (!companyError && companyData && (companyData as any)[CompanySettingKey.MAX_OVERTIME_HOURS]) {
          maxOvertime = (companyData as any)[CompanySettingKey.MAX_OVERTIME_HOURS]
        }
      } else {
        // If no company ID, get default from companies table
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select(CompanySettingKey.MAX_OVERTIME_HOURS)
          .limit(1)
          .maybeSingle()
        
        if (!companyError && companyData && (companyData as any)[CompanySettingKey.MAX_OVERTIME_HOURS]) {
          maxOvertime = (companyData as any)[CompanySettingKey.MAX_OVERTIME_HOURS]
        }
      }
      
      // Get approved requests for this employee in the current month
      const now = new Date()
      const currentMonth = now.getMonth()
      const currentYear = now.getFullYear()
      
      const { data: approvedRequests, error: requestsError } = await supabase
        .from('overtime_requests')
        .select('hours, date')
        .eq('employee_id', employeeId)
        .eq('status', OvertimeStatus.APPROVED)
      
      if (requestsError) {
        throw new Error("Could not check existing overtime hours")
      }
      
      if (!approvedRequests) {
        return { 
          requiresVPApproval: true, // Be conservative if we can't verify
          currentMonthlyHours: 0,
          maxAllowedHours: maxOvertime
        }
      }
      
      // Calculate total hours for this month
      const monthlyHours = approvedRequests.reduce((total: number, req: { hours: number; date: string }) => {
        const reqDate = new Date(req.date)
        if (reqDate.getMonth() === currentMonth && reqDate.getFullYear() === currentYear) {
          return total + req.hours
        }
        return total
      }, 0)
      
      // Check if this request would exceed the max overtime
      return { 
        requiresVPApproval: (monthlyHours + hours) > maxOvertime,
        currentMonthlyHours: monthlyHours,
        maxAllowedHours: maxOvertime
      }
    } catch (error: unknown) {
      toast.error("Error checking overtime eligibility")
      return { 
        requiresVPApproval: true, // Conservative approach if we can't check
        error: error instanceof Error ? error.message : undefined
      };
    }
  }
  
  /**
   * Fetch all overtime requests relevant to the user
   */
  const fetchRequests = async (): Promise<boolean> => {
    if (!user?.id) {
      setError("User ID is missing")
      return false
    }

    setLoading(true)
    setError(null)

    try {
      
      let query = supabase
        .from('overtime_requests')
        .select(`
          *,
          overtime_reasons!justification(
            main_title,
            reason
          )
        `)
        .order('submitted_at', { ascending: false })

      // Filter based on user role and department
      // For department_head, superintendent, and plant_manager roles, filter by their assigned departments
      if ((user.role === UserRole.DEPARTMENT_HEAD || user.role === UserRole.SUPERINTENDENT || user.role === UserRole.PLANT_MANAGER) && 
          user.department && user.department.length > 0) {
        // Assuming 'overtime_requests.department' is a single text field,
        // and we want to fetch requests where this field is one of the user's departments.
        query = query.in('department', user.department)
      }

      const { data, error } = await query

      if (error) {
        throw error
      }

      if (!data) {
        setOvertimeRequests([])
        return true
      }

      // Transform the data to match our frontend model
      const transformedRequests = data.map((request: any) => {
        return {
          ...request,
          employeeId: request.employee_id,
          employeeName: request.employee_name,
          startTime: request.start_time,
          endTime: request.end_time,
          submittedAt: request.submitted_at,
          updatedAt: request.updated_at,
          vpApprovalRequired: request.vp_approval_required,
          approverId: request.approver_id,
          approverName: request.approver_name,
          approverComment: request.approver_comment,
          created_by_id: request.created_by_id,
          created_by_name: request.created_by_name,
          created_by_role: request.created_by_role,
          justification_text: (() => {
            // Extract justification text - prioritize reason column
            if (request.overtime_reasons) {
              if (Array.isArray(request.overtime_reasons) && request.overtime_reasons.length > 0) {
                // Handle array format (inner join)
                return request.overtime_reasons[0].reason || request.overtime_reasons[0].main_title || 'Unknown reason';
              } else if (typeof request.overtime_reasons === 'object' && request.overtime_reasons !== null) {
                // Handle object format (regular join)
                const reasonObj = request.overtime_reasons as OvertimeReason;
                return reasonObj.reason || reasonObj.main_title || 'Unknown reason';
              }
            }
            return 'Unknown reason';
          })()
        }
      })

      setOvertimeRequests(transformedRequests)
      return true

    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err)
      setError(`Failed to fetch overtime requests: ${errorMessage}`)
      return false
    } finally {
      setLoading(false)
    }
  }
  
  return {
    submitOvertimeRequest,
    checkEmployeeOvertimeEligibility,
    fetchRequests
  }
}
